import { LogOutIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";

import { Logo } from "@/components/shared";

import { useLogoutAlert } from "@/hooks";

import { useRole } from "@/store";

import { visibleSections } from "../nav-config";
import { NavAccordion } from "./nav-accordion";
import { NavAnchor } from "./nav-anchor";
import { NavFlyout } from "./nav-flyout";
import { ACTIVE_MARKER } from "./nav-styles";
import { isBranch } from "./nav-tree";
import { toNavNode } from "./to-nav-node";

/**
 * Admin panelining yon paneli. Kabinetdagi (`learn-up-frontend`) yon panel
 * bilan bir xil ko'rinishda: bolali band, ochiluvchi menyu va havola
 * komponentlari ikkala repoda bir xil fayllar, farq faqat menyu sozlamasi,
 * logotip va `nav-anchor.tsx` da.
 */
export function AppSidebar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const role = useRole();
  const { isMobile, setOpenMobile, state } = useSidebar();
  const openLogout = useLogoutAlert(store => store.onOpen);

  // Mobil sheet o'zi yopilmaydi: havolaga bosilgach panel ochiq qolib,
  // yangi sahifani to'sib turardi.
  const closeMobile = () => {
    if (isMobile) setOpenMobile(false);
  };

  // Ochiluvchi menyu faqat ish stolidagi yig'ilgan panelda. Telefonda panel
  // har doim to'liq kenglikdagi sheet — u yerda bolalar ro'yxat bo'lib ochiladi.
  const collapsed = state === "collapsed" && !isMobile;
  const sections = visibleSections(role);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="h-14 justify-center px-3">
        <Logo collapsed={collapsed} />
      </SidebarHeader>

      {/* shadcn yig'ilgan panelda kontentni `overflow-hidden` qiladi va
          pastki bandlarga (Tizim bo'limi) umuman yetib bo'lmasdi. Faqat
          vertikal o'q ochiladi: ko'rinmas bo'lim sarlavhalari 3rem dan keng
          va gorizontal aylantirishni paydo qilardi. */}
      <SidebarContent className="group-data-[collapsible=icon]:overflow-x-hidden group-data-[collapsible=icon]:overflow-y-auto">
        {sections.map(section => (
          <SidebarGroup key={section.labelKey}>
            <SidebarGroupLabel className="text-[11px] font-semibold tracking-wider uppercase">
              {t(section.labelKey)}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map(item => {
                  const node = toNavNode(item, pathname, key => t(key));

                  if (isBranch(node)) {
                    return collapsed ? (
                      <NavFlyout key={node.key} node={node} />
                    ) : (
                      <NavAccordion key={node.key} node={node} onNavigate={closeMobile} />
                    );
                  }

                  return (
                    <SidebarMenuItem
                      key={node.key}
                      data-active={node.active}
                      className={ACTIVE_MARKER}
                    >
                      {/* Havola tugmaning O'ZI (`asChild`): fokus halqasi va
                          yig'ilgan paneldagi tooltip klaviaturada ham chiqadi. */}
                      <SidebarMenuButton asChild isActive={node.active} tooltip={node.label}>
                        <NavAnchor
                          href={node.href}
                          aria-current={node.active ? "page" : undefined}
                          onClick={closeMobile}
                        >
                          {node.icon && <node.icon />}
                          <span>{node.label}</span>
                        </NavAnchor>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      {/* Chiqish menyuning ENG PASTIDA: u navigatsiya emas, va bo'limlar
          orasida turgani "yana bitta sahifa" degan taassurot qoldirardi.
          Sarlavhadagi menyuda ham qoladi — odamlar uni ikkala joydan
          qidiradi. Tasdiqlash oynasi `<ConfirmLogout />` da, u qobiqda
          bir marta chiziladi.

          Rang ikkala ilovada bir xil — qizil emas, oq: `text-destructive`
          yorug' mavzudagi yashil panel ustida ~1:1 kontrast beradi (admin
          panelida shunday edi, yozuv deyarli ko'rinmasdi). Oqning 90% i —
          4.8:1 (WCAG AA; 85% i 4.49:1 bilan chegaradan sal pastda edi).
          Qizil faqat ustiga kelganda, fonda. */}
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              className="text-sidebar-foreground/90 hover:bg-destructive/20 hover:text-sidebar-foreground"
              tooltip={t("AUTH.LOGOUT")}
              onClick={openLogout}
            >
              <LogOutIcon />
              <span>{t("AUTH.LOGOUT")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
